import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';

import { MAX_UPLOAD_IMAGE_SIZE } from '@/constants/common';
import { Post } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { QueryKey } from '@/types/queryKey';
import { attachImageToPost, deletePostImage } from '@/lib/services/posts';
import { useAxiosErrorHandler } from '@/hooks/common';
import useProjectId from '@/hooks/useProjectId.ts';

export function usePostFileUpload(post?: Post) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const { parseAndShowError } = useAxiosErrorHandler();

  const projectId = useProjectId();

  const postImage = post?.images?.length && post.images[0].url;
  const postImageId = post?.images?.length && post.images[0].id;

  const deleteAttachedImage = useCallback(async () => {
    if (!post?.id || !projectId || !postImageId) {
      parseAndShowError('post id or project id or image id is no defined');
      return;
    }

    setIsLoading(true);
    try {
      const res = await deletePostImage(projectId, post.id, postImageId);
      if (res instanceof AxiosError) {
        parseAndShowError(res);
        setIsLoading(false);
        return;
      }

      setImagePreview(null);
      queryClient.invalidateQueries({ queryKey: [QueryKey.Post] }).then(() => {
        setIsLoading(false);
      });
    } finally {
      setIsLoading(false);
    }
  }, [post?.id, projectId, postImageId, parseAndShowError, queryClient]);

  const uploadAndAttachImage = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!post?.id || !projectId) {
        console.error('post id or project id is no defined');
        return;
      }

      const file = handleFileChange(event);

      if (file) {
        setIsLoading(true);
        try {
          if (postImageId) {
            const res = await deletePostImage(projectId, post.id, postImageId);
            if (res instanceof AxiosError) {
              parseAndShowError(res);
              setIsLoading(false);
              return;
            }
          }

          const response = await attachImageToPost(projectId, post.id, file);
          if (response.error) {
            parseAndShowError(response.error);
          }

          setImagePreview(URL.createObjectURL(file));

          setIsLoading(false);
          return response;
        } finally {
          setIsLoading(false);
        }
      }
    },
    [post?.id, projectId, parseAndShowError, postImageId]
  );

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_UPLOAD_IMAGE_SIZE * 1024 * 1024) {
      toast.error(`File size should be less than ${MAX_UPLOAD_IMAGE_SIZE} MB.`);
      setImagePreview(null);
      return;
    }

    return file;
  };

  return {
    fileInputRef,
    imagePreview: imagePreview || postImage,
    setImagePreview,
    handleFileChange,
    handleFileUploadClick,
    uploadAndAttachImage,
    isLoading,
    isHovered,
    setIsHovered,
    deleteAttachedImage,
  };
}
